import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast, TOAST_ERROR, TOAST_SUCCESS, FIVE_SECONDS, TEN_SECONDS } from 'lib/utils';
import { makeRemoteDeleteQueueRequest } from 'main/factories/usecases';

type DeleteQueueRequestData = {
  requestId: string;
  locationId?: string;
};

const deleteRequest = async (data: DeleteQueueRequestData) => {
  const service = makeRemoteDeleteQueueRequest(data.requestId);
  const result = await service.delete();
  return result;
};

/**
 * Hook for deleting a queue request (e.g., dissolving a group)
 * Automatically invalidates queue queries after successful deletion.
 *
 * @param onSuccessCallback - Optional callback called after successful deletion
 */
export const useDeleteQueueRequestMutation = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRequest,
    onSuccess: (_data, variables) => {
      const locationId = variables.locationId;

      // Invalidate queue queries - deleted request should be removed from all views
      if (locationId) {
        queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
        queryClient.invalidateQueries({ queryKey: ['queueRequestsSummary', locationId] });
        queryClient.invalidateQueries({
          predicate: query => query.queryKey[0] === 'queueRequests' && query.queryKey[3] === locationId,
        });
        queryClient.invalidateQueries({ queryKey: ['flaggedTransactions', locationId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['queueRequestsCount'] });
        queryClient.invalidateQueries({ queryKey: ['queueRequestsSummary'] });
        queryClient.invalidateQueries({ predicate: query => query.queryKey[0] === 'queueRequests' });
        queryClient.invalidateQueries({ queryKey: ['flaggedTransactions'] });
      }

      showToast('Group dissolved successfully', TOAST_SUCCESS, FIVE_SECONDS);

      if (onSuccessCallback) onSuccessCallback();
    },
    onError: (error: any) => {
      showToast(error.message || 'Error while deleting request', TOAST_ERROR, TEN_SECONDS);
    },
  });
};
